import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';

import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import { toast } from '@src/components/core/toaster';
import {
  createMySetupIntent,
  fetchMyBillingLiveStatus,
  fetchMyPaymentMethods,
  listMyBillingProfiles,
  listMyThresholdCharges,
  setMyDefaultPaymentMethod,
  updateMyThreshold,
} from '@src/api/myBilling';

const niceStep = (max) => {
  if (max <= 1500) return 50;
  if (max <= 5000) return 100;
  if (max <= 10000) return 250;
  return 500;
};

const formatCurrency0dp = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const STATE_CHIP_COLORS = {
  healthy: 'success',
  warning: 'warning',
  paused: 'error',
  disabled: 'default',
};

const OUTCOME_CHIP_COLORS = {
  pending: 'default',
  charged: 'success',
  retried_success: 'success',
  failed: 'error',
  paused: 'error',
};

const PAYMENT_METHOD_LABELS = {
  card: 'Card only',
  ach: 'ACH (direct debit)',
  card_then_ach: 'Card, fall back to ACH',
  ach_then_card: 'ACH, fall back to card',
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

// ── Add Card dialog (Stripe Elements) ─────────────────────────────────

function AddCardForm({ clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) },
    });
    if (result.error) {
      setError(result.error.message);
      setSaving(false);
      return;
    }
    if (result.setupIntent?.status === 'succeeded') {
      toast.success('Card saved.');
      onSuccess(result.setupIntent.payment_method);
    } else {
      setError('Setup did not complete. Please try again.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Your card information is encrypted by Stripe before it leaves your
          browser. We never see your full card number.
        </DialogContentText>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#333' },
                invalid: { color: '#dc2626' },
              },
            }}
          />
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={!stripe || saving}>
          {saving ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </form>
  );
}

function AddCardDialog({ open, onClose, profileId, isFirstCard, onAttached }) {
  const [intent, setIntent] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !profileId) return undefined;
    let cancelled = false;
    setIntent(null);
    setLoadError(null);
    createMySetupIntent(profileId, 'card')
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data;
        if (!data?.client_secret || !data?.publishable_key) {
          setLoadError('Unable to start payment setup. Please try again later.');
          return;
        }
        setIntent(data);
        setStripePromise((prev) => prev || loadStripe(data.publishable_key));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.detail || err?.message || 'Failed to start setup.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, profileId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a card</DialogTitle>
      {loadError ? (
        <>
          <DialogContent>
            <Alert severity="error">{loadError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : !intent || !stripePromise ? (
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Securing your connection to Stripe…
            </Typography>
          </Stack>
        </DialogContent>
      ) : (
        <Elements stripe={stripePromise}>
          <AddCardForm
            clientSecret={intent.client_secret}
            onSuccess={async (newPmId) => {
              if (isFirstCard && newPmId) {
                try {
                  await setMyDefaultPaymentMethod(profileId, newPmId);
                } catch {
                  /* non-fatal */
                }
              }
              onAttached();
              onClose();
            }}
            onCancel={onClose}
          />
        </Elements>
      )}
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────

export default function MyBillingPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(null);
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Keep slider value in sync with the server's threshold when live status loads/reloads.
  useEffect(() => {
    if (liveStatus?.threshold_amount != null) {
      setSliderValue(Number(liveStatus.threshold_amount));
    }
  }, [liveStatus?.threshold_amount]);

  const locked = Boolean(liveStatus?.threshold_locked_by_staff);
  const bounds = liveStatus?.threshold_bounds;
  const sliderMin = bounds ? Math.floor(Number(bounds.min)) : 500;
  const sliderMax = bounds ? Math.ceil(Number(bounds.max)) : 10000;
  const sliderStep = niceStep(sliderMax);
  const sliderDirty =
    sliderValue != null &&
    Number(sliderValue) !== Number(liveStatus?.threshold_amount || 0);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === selectedProfileId) || null,
    [profiles, selectedProfileId],
  );

  const refreshPaymentMethods = useCallback(async () => {
    if (!selectedProfileId) return;
    setMethodsLoading(true);
    try {
      const resp = await fetchMyPaymentMethods(selectedProfileId);
      setPaymentMethods(resp?.data || null);
    } catch {
      setPaymentMethods(null);
    } finally {
      setMethodsLoading(false);
    }
  }, [selectedProfileId]);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listMyBillingProfiles();
      const data = resp?.data;
      const rows = Array.isArray(data) ? data : data?.results || [];
      setProfiles(rows);
      if (rows.length && !selectedProfileId) {
        setSelectedProfileId(rows[0].id);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [selectedProfileId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Fetch live status + payment methods + ledger when the selected profile changes.
  useEffect(() => {
    if (!selectedProfileId) {
      setLiveStatus(null);
      setPaymentMethods(null);
      setLedgerEntries([]);
      return;
    }
    fetchMyBillingLiveStatus(selectedProfileId)
      .then((resp) => setLiveStatus(resp?.data || null))
      .catch(() => setLiveStatus(null));
    setMethodsLoading(true);
    fetchMyPaymentMethods(selectedProfileId)
      .then((resp) => setPaymentMethods(resp?.data || null))
      .catch(() => setPaymentMethods(null))
      .finally(() => setMethodsLoading(false));
    setLedgerLoading(true);
    listMyThresholdCharges({ client_id: selectedProfile?.client })
      .then((resp) => {
        const data = resp?.data;
        setLedgerEntries(Array.isArray(data) ? data : data?.results || []);
      })
      .catch(() => setLedgerEntries([]))
      .finally(() => setLedgerLoading(false));
  }, [selectedProfileId, selectedProfile?.client]);

  // Light polling for live spend.
  useEffect(() => {
    if (!selectedProfileId) return undefined;
    const interval = setInterval(() => {
      fetchMyBillingLiveStatus(selectedProfileId)
        .then((resp) => setLiveStatus(resp?.data || null))
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(interval);
  }, [selectedProfileId]);

  const progressPct = useMemo(() => {
    const v = liveStatus?.threshold_progress_pct;
    if (typeof v !== 'number') return 0;
    return Math.max(0, Math.min(100, v));
  }, [liveStatus]);

  const handleSaveThreshold = async () => {
    if (!selectedProfileId || sliderValue == null) return;
    setSavingThreshold(true);
    try {
      const resp = await updateMyThreshold(selectedProfileId, sliderValue);
      toast.success(`Threshold updated to ${formatCurrency(resp.data.threshold_amount)}.`);
      // Refresh live status so the new threshold + progress bar reflect.
      const statusResp = await fetchMyBillingLiveStatus(selectedProfileId);
      setLiveStatus(statusResp?.data || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || 'Could not update threshold.',
      );
    } finally {
      setSavingThreshold(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profiles.length) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          You don't have a billing profile yet. Please reach out to your account
          manager to get set up.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Billing</Typography>
          <Typography variant="body2" color="text.secondary">
            Your current threshold billing settings and charge history
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={loadProfiles}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Profile selector (only if multiple accessible profiles) */}
      {profiles.length > 1 && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <TextField
              select
              fullWidth
              label="Client"
              value={selectedProfileId || ''}
              onChange={(e) => setSelectedProfileId(Number(e.target.value))}
            >
              {profiles.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.client_name}
                </MenuItem>
              ))}
            </TextField>
          </CardContent>
        </Card>
      )}

      {/* CARD 1 — Current billing status */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Current status" />
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Chip
              label={liveStatus?.state_display || 'Healthy'}
              color={STATE_CHIP_COLORS[liveStatus?.state || 'healthy']}
              sx={{ minWidth: 100 }}
            />
            <Box sx={{ flex: 1, width: '100%' }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Unbilled ad spend
                </Typography>
                <Typography variant="caption">
                  {formatCurrency(liveStatus?.unbilled_spend)} /{' '}
                  {formatCurrency(liveStatus?.threshold_amount)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                color={
                  progressPct >= 100 ? 'error' : progressPct >= 75 ? 'warning' : 'primary'
                }
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </Stack>

          <Stack spacing={2}>
            {/* Threshold slider (or read-only when locked) */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Charge threshold
                  </Typography>
                  {locked && (
                    <Tooltip title="Your threshold is locked. Contact your account manager to adjust it.">
                      <LockIcon fontSize="inherit" color="action" />
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="h6">
                  {formatCurrency0dp(sliderValue ?? liveStatus?.threshold_amount)}
                </Typography>
              </Stack>
              {bounds && (
                <>
                  <Slider
                    value={Number(sliderValue ?? liveStatus?.threshold_amount ?? sliderMin)}
                    onChange={(_, value) => setSliderValue(value)}
                    disabled={locked || savingThreshold}
                    min={sliderMin}
                    max={sliderMax}
                    step={sliderStep}
                    marks={[
                      { value: sliderMin, label: formatCurrency0dp(sliderMin) },
                      { value: sliderMax, label: formatCurrency0dp(sliderMax) },
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => formatCurrency0dp(v)}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {locked
                        ? 'Locked by your account manager.'
                        : 'Drag to adjust how often you get charged.'}
                    </Typography>
                    {sliderDirty && !locked && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveThreshold}
                        disabled={savingThreshold}
                      >
                        {savingThreshold ? 'Saving…' : 'Save threshold'}
                      </Button>
                    )}
                  </Stack>
                </>
              )}
            </Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Payment method
              </Typography>
              <Typography variant="body2">
                {PAYMENT_METHOD_LABELS[selectedProfile?.payment_method_priority] ||
                  selectedProfile?.payment_method_priority ||
                  '—'}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Last charge
              </Typography>
              <Typography variant="body2">
                {formatDateTime(liveStatus?.last_threshold_charge_at)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* CARD 2 — Payment methods */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Payment methods"
          action={
            <Button
              size="small"
              variant="contained"
              startIcon={<AddCardIcon />}
              onClick={() => setAddCardOpen(true)}
            >
              Add card
            </Button>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {methodsLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Stack>
          ) : paymentMethods?.note ? (
            <Alert severity="info">{paymentMethods.note}</Alert>
          ) : paymentMethods?.methods?.length ? (
            <Stack spacing={1.5}>
              {paymentMethods.methods.map((m) => (
                <Stack
                  key={m.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{m.label}</Typography>
                    {m.is_default && (
                      <Chip size="small" label="Primary" color="success" />
                    )}
                  </Stack>
                  {!m.is_default && (
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          await setMyDefaultPaymentMethod(selectedProfileId, m.id);
                          toast.success('Primary card updated.');
                          refreshPaymentMethods();
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail ||
                              err?.message ||
                              'Could not update primary.',
                          );
                        }
                      }}
                    >
                      Make primary
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No payment methods yet. Click <strong>Add card</strong> to get started.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* CARD 3 — Charge history */}
      <Card>
        <CardHeader title="Charge history" />
        <CardContent sx={{ pt: 0 }}>
          {ledgerLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Stack>
          ) : ledgerEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No charges yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerEntries.slice(0, 25).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDateTime(row.triggered_at)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.cumulative_spend)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.outcome_display || row.outcome}
                          color={OUTCOME_CHIP_COLORS[row.outcome] || 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <AddCardDialog
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        profileId={selectedProfileId}
        isFirstCard={!paymentMethods?.methods?.length}
        onAttached={refreshPaymentMethods}
      />
    </Container>
  );
}
